import { useEffect, useState } from "react";
import { ShieldAlert, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export function ImpersonationBanner() {
  const { markIntentionalSignOut } = useAuth();
  const [impersonation, setImpersonation] = useState<{ active: boolean; userName: string; userId: string; expiresAt?: number } | null>(null);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const read = () => {
      // Prefer localStorage; fall back to sessionStorage if it was cleared mid-session.
      const raw =
        localStorage.getItem("impersonation") ||
        sessionStorage.getItem("impersonation");
      if (!raw) return;
      try {
        const parsed = JSON.parse(raw);
        if (!parsed?.active) return;
        if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
          handleExit();
          return;
        }
        // Mirror to both storages so either can recover the other.
        localStorage.setItem("impersonation", raw);
        sessionStorage.setItem("impersonation", raw);
        setImpersonation(parsed);
      } catch {
        localStorage.removeItem("impersonation");
        sessionStorage.removeItem("impersonation");
      }
    };
    read();
    window.addEventListener("storage", read);
    return () => window.removeEventListener("storage", read);
  }, []);

  const handleExit = async () => {
    setExiting(true);
    markIntentionalSignOut();
    localStorage.removeItem("impersonation");
    sessionStorage.removeItem("impersonation");

    try {
      await supabase.auth.signOut({ scope: "global" });
    } catch {
      // Force redirect even if signOut fails
    }

    window.location.replace("/auth");
  };

  // Full-screen exit overlay
  if (exiting) {
    return (
      <div className="fixed inset-0 z-[200] bg-background flex flex-col items-center justify-center gap-4">
        <div className="rounded-full bg-destructive/10 p-4">
          <ShieldAlert className="w-8 h-8 text-destructive" />
        </div>
        <div className="text-center space-y-2">
          <h2 className="text-sm font-semibold text-foreground">Ending Impersonation</h2>
          <p className="text-xs text-muted-foreground">Signing out and returning to your admin session…</p>
        </div>
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mt-2" />
      </div>
    );
  }

  if (!impersonation?.active) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-destructive text-destructive-foreground px-4 py-2 flex items-center justify-center gap-3">
      <ShieldAlert className="w-4 h-4" />
      <span className="text-xs font-bold uppercase tracking-wide">
        Admin Mode — Impersonating {impersonation.userName}
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={handleExit}
        className="h-6 px-2 text-[10px] bg-transparent border-destructive-foreground/30 text-destructive-foreground hover:bg-destructive-foreground/10"
      >
        <X className="w-3 h-3 mr-1" /> Exit
      </Button>
    </div>
  );
}
